import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * ruts: registro global (delegado) para el widget "Guía de Tallas"
 * (blocks/ruts-size-calculator.liquid).
 *
 * Ese bloque solo se renderiza de forma natural en la ficha de producto, así
 * que su propio <script> inline solo se ejecuta ahí. Cuando Quick Add clona
 * el mismo HTML (cloneNode/DOMParser) para mostrarlo en el modal, el <script>
 * clonado nunca se re-ejecuta -- es una restricción del navegador -- y en
 * páginas de colección (donde el bloque nunca aparece de forma natural) esa
 * inicialización no llega a ocurrir en absoluto, ni siquiera delegada.
 *
 * quick-add.js sí se carga siempre (es un módulo real, no un <script> de
 * bloque), así que aquí SÍ se ejecuta de forma fiable en cualquier página.
 * Con delegación a document, el listener registrado aquí maneja los clics de
 * cualquier instancia del widget -- tanto si viene de la ficha de producto
 * como de un clon dentro de Quick Add -- sin duplicar el cálculo ni la
 * lógica visual, que siguen siendo exactamente las mismas (bugfix 2026-08-15).
 */
if (!window.rutsSizeCalcBound) {
  window.rutsSizeCalcBound = true;

  const RUTS_SC_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const RUTS_SC_BOUNDS = {
    pecho: [88, 92, 96, 100, 104, Infinity],
    cintura: [74, 78, 82, 86, 90, Infinity],
    altura: [170, 176, 182, 188, 194, Infinity],
  };
  function rutsSizeIndex(value, bounds) {
    for (let i = 0; i < bounds.length; i++) {
      if (value <= bounds[i]) return i;
    }
    return bounds.length - 1;
  }

  const RUTS_SC_EU_BOUNDS = [23.0, 23.5, 24.0, 24.5, 25.0, 25.5, 26.0, 26.7, 27.3, 28.0, 28.7];
  function rutsFootIndex(value) {
    for (let i = 0; i < RUTS_SC_EU_BOUNDS.length; i++) {
      if (value < RUTS_SC_EU_BOUNDS[i]) return i;
    }
    return RUTS_SC_EU_BOUNDS.length - 1;
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openTrigger = target.closest('[data-ruts-open]');
    if (openTrigger) {
      const dlgOpen = document.getElementById('ruts-sc-dlg-' + openTrigger.getAttribute('data-ruts-open'));
      if (dlgOpen instanceof HTMLDialogElement) {
        if (dlgOpen.showModal) {
          dlgOpen.showModal();
        } else {
          dlgOpen.setAttribute('open', '');
        }
      }
      return;
    }

    const closeTrigger = target.closest('[data-ruts-close]');
    if (closeTrigger) {
      const dlgClose = document.getElementById('ruts-sc-dlg-' + closeTrigger.getAttribute('data-ruts-close'));
      if (dlgClose instanceof HTMLDialogElement) dlgClose.close();
      return;
    }

    if (target instanceof HTMLDialogElement && target.classList.contains('ruts-sc__dialog')) {
      target.close();
      return;
    }

    const tabTrigger = target.closest('[data-ruts-tab]');
    if (tabTrigger) {
      const tabRoot = tabTrigger.closest('.ruts-sc');
      if (!tabRoot) return;
      const name = tabTrigger.getAttribute('data-ruts-tab');
      tabRoot.querySelectorAll('[data-ruts-tab]').forEach((t) => t.classList.toggle('is-active', t === tabTrigger));
      tabRoot
        .querySelectorAll('[data-ruts-panel]')
        .forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-ruts-panel') === name));
      return;
    }

    const calcBtn = target.closest('[data-ruts-calc]');
    if (calcBtn) {
      const calcRoot = calcBtn.closest('.ruts-sc');
      if (!calcRoot) return;
      const votes = [];
      ['pecho', 'cintura', 'altura'].forEach((key) => {
        const el = calcRoot.querySelector(`[data-ruts-input="${key}"]`);
        const v = parseFloat(el && el.value);
        if (!isNaN(v) && v > 0) votes.push(rutsSizeIndex(v, RUTS_SC_BOUNDS[key]));
      });

      const errEl = calcRoot.querySelector('[data-ruts-error]');
      const resEl = calcRoot.querySelector('[data-ruts-result]');
      if (votes.length === 0) {
        errEl.hidden = false;
        resEl.hidden = true;
        return;
      }
      errEl.hidden = true;

      const counts = {};
      votes.forEach((idx) => {
        counts[idx] = (counts[idx] || 0) + 1;
      });
      let best = -1,
        bestCount = -1;
      Object.keys(counts).forEach((k) => {
        const idx = parseInt(k, 10);
        if (counts[idx] > bestCount || (counts[idx] === bestCount && idx > best)) {
          best = idx;
          bestCount = counts[idx];
        }
      });

      const size = RUTS_SC_SIZES[best];
      calcRoot.querySelector('[data-ruts-badge]').textContent = size;
      resEl.hidden = false;

      calcRoot
        .querySelectorAll('[data-ruts-row]')
        .forEach((r) => r.classList.toggle('is-active', r.getAttribute('data-ruts-row') === size));
      return;
    }

    const calcFootBtn = target.closest('[data-ruts-calc-foot]');
    if (calcFootBtn) {
      const footRoot = calcFootBtn.closest('.ruts-sc');
      if (!footRoot) return;
      const elFoot = footRoot.querySelector('[data-ruts-input-foot]');
      const vFoot = parseFloat(elFoot && elFoot.value);
      const errElFoot = footRoot.querySelector('[data-ruts-error-foot]');
      const resElFoot = footRoot.querySelector('[data-ruts-result-foot]');
      if (isNaN(vFoot) || vFoot <= 0) {
        errElFoot.hidden = false;
        resElFoot.hidden = true;
        return;
      }
      errElFoot.hidden = true;

      const eu = 36 + rutsFootIndex(vFoot);
      const uk = eu - 33;
      const us = eu - 32;
      footRoot.querySelector('[data-ruts-badge-foot]').textContent = 'Talla EU ' + eu;
      footRoot.querySelector('[data-ruts-sub-foot]').textContent = 'UK ' + uk + ' · US ' + us;
      resElFoot.hidden = false;

      footRoot
        .querySelectorAll('[data-ruts-row-foot]')
        .forEach((r) => r.classList.toggle('is-active', r.getAttribute('data-ruts-row-foot') === String(eu)));
      return;
    }
  });
}
